import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import EmailCaptureForm from "@/components/common/emailCaptureForm";
import TagPill from "@/components/common/tagPill";

interface RelatedTag {
  name: string;
  slug: string;
  bgColor?: string | null;
}

interface HeroStats {
  jobs?: number | null;
}

interface PrimaryCta {
  label: string;
  /** Anchor (e.g. "#jobList") or full URL. Rendered as a plain <a>. */
  href: string;
}

interface HeroProps {
  /** H1 content. ReactNode so callers can colour-accent specific words. */
  title?: ReactNode;
  /** Subtitle content. */
  subtitle?: ReactNode;
  /** Related tags rendered below the form ("People also search for"). */
  relatedTags?: RelatedTag[];
  /** Hide the "browse all jobs" link (e.g., on search-style pages). */
  hideBrowseLink?: boolean;
  /** Live counters for the stat eyebrow. */
  stats?: HeroStats;
  /**
   * Omit the above-fold email capture form. Tag pages use this to keep the
   * job results visible, relying on the sidebar + post-list variants for
   * capture instead.
   */
  hideEmailForm?: boolean;
  /**
   * Primary CTA rendered where the email form would be. Typical use: a
   * "Browse {N} jobs" scroll-anchor to the jobs list on tag pages.
   */
  primaryCta?: PrimaryCta;
}

const formatCount = (n: number | null | undefined) =>
  typeof n === "number" ? n.toLocaleString() : null;

export default function Hero({
  title,
  subtitle,
  relatedTags,
  hideBrowseLink = false,
  stats,
  hideEmailForm = false,
  primaryCta,
}: HeroProps) {
  const jobs = formatCount(stats?.jobs);

  return (
    <div className="mt-16 flex flex-col text-center sm:mt-20">
      <header>
        <div className="px-4 sm:px-6 lg:px-8">
          {jobs && (
            <p className="mx-auto mb-4 font-mono text-xs tracking-wide text-gray-500 uppercase tabular-nums">
              {jobs} open roles
            </p>
          )}

          <h1 className="mx-auto mb-5 max-w-[20ch] text-4xl font-semibold tracking-tight text-balance text-gray-900 md:text-6xl lg:text-7xl">
            {title ?? (
              <>
                Build the{" "}
                <span className="text-polaris-orange">data career</span> you
                want
              </>
            )}
          </h1>

          <p className="mx-auto my-2 max-w-[56ch] text-lg text-pretty text-gray-500 md:text-xl">
            {subtitle ?? (
              <>
                Every role tagged by stack, seniority, and remote, so your next
                move is easier to find than your last one.
              </>
            )}
          </p>
        </div>

        {!hideEmailForm && <EmailCaptureForm />}

        {primaryCta && (
          <div className="mt-8 flex justify-center">
            <a
              href={primaryCta.href}
              className="focus-visible:ring-polaris-orange/40 bg-polaris-navy inline-flex items-center gap-2 rounded-lg px-6 py-3 text-base font-semibold text-white shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:outline-none"
            >
              {primaryCta.label}
              <span aria-hidden="true">↓</span>
            </a>
          </div>
        )}

        {!hideBrowseLink && (
          <div className="mt-4 flex justify-center">
            <Link
              to="/search"
              className="group text-sm text-gray-400 transition-colors hover:text-gray-600"
            >
              or browse all jobs
              <span className="ml-0.5 inline-block transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </Link>
          </div>
        )}

        {relatedTags && relatedTags.length > 0 && (
          <div className="mx-auto mt-10 max-w-4xl px-4">
            <p className="mb-3 text-sm text-gray-400">People also search for</p>
            <div className="flex flex-wrap justify-center gap-2">
              {relatedTags.map((tag) => (
                <TagPill
                  key={tag.slug}
                  variant="filter"
                  size="sm"
                  bgColor={tag.bgColor}
                  to="/$slug"
                  params={{ slug: tag.slug }}
                >
                  {tag.name}
                </TagPill>
              ))}
            </div>
          </div>
        )}
      </header>
    </div>
  );
}
